/**
 * Template Builder — WhatsApp message templates (shadcn/ui)
 */

import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2, Eye, Save, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';
import {
  WhatsAppTemplate,
  WhatsAppTemplateCategory,
  TemplateCreateRequest,
  WhatsAppTemplateButton,
  WhatsAppConstraints,
} from '../../types/whatsapp-templates';
import {
  fetchWhatsAppVariableMetadata,
  WhatsAppVariableMetadata,
  VariableMetadataItem,
  CustomVariableItem,
} from '../../services/whatsapp-template-metadata-api';
import whatsappTemplatesApi from '../../services/whatsapp-templates-api';

const HEADER_TYPES = [
  { value: 'NONE' as const, label: 'None' },
  { value: 'TEXT' as const, label: 'Text' },
  { value: 'IMAGE' as const, label: 'Image' },
  { value: 'VIDEO' as const, label: 'Video' },
  { value: 'DOCUMENT' as const, label: 'Document' },
];

interface TemplateBuilderProps {
  accountId?: number;
  channelId?: number;
  initialTemplate?: WhatsAppTemplate;
  onSave: (template: TemplateCreateRequest) => Promise<void>;
  onCancel: () => void;
  constraints?: WhatsAppConstraints;
}

const TemplateBuilder: React.FC<TemplateBuilderProps> = ({
  accountId,
  channelId,
  initialTemplate,
  onSave,
  onCancel,
  constraints,
}) => {
  const [variableMetadata, setVariableMetadata] = useState<WhatsAppVariableMetadata | null>(null);
  const [variableMetadataLoading, setVariableMetadataLoading] = useState(false);

  const [name, setName] = useState(initialTemplate?.name || '');
  const [category, setCategory] = useState<WhatsAppTemplateCategory>(
    initialTemplate?.category || WhatsAppTemplateCategory.UTILITY
  );
  const [language, setLanguage] = useState(initialTemplate?.language || 'en');
  const [body, setBody] = useState(initialTemplate?.body || '');
  const [footer, setFooter] = useState(initialTemplate?.footer || '');

  const [headerType, setHeaderType] = useState<'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT'>(
    initialTemplate?.header ? initialTemplate.header.type : 'NONE'
  );
  const [headerText, setHeaderText] = useState(initialTemplate?.header?.text || '');
  const [headerMediaUrl, setHeaderMediaUrl] = useState(initialTemplate?.header?.media_url || '');

  const [buttons, setButtons] = useState<WhatsAppTemplateButton[]>(initialTemplate?.buttons || []);
  const [variables, setVariables] = useState<string[]>(initialTemplate?.variables || []);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [nameCheckLoading, setNameCheckLoading] = useState(false);
  const [nameCheckAvailable, setNameCheckAvailable] = useState<boolean | null>(null);
  const [nameCheckMessage, setNameCheckMessage] = useState<string>('');

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    setVariableMetadataLoading(true);
    fetchWhatsAppVariableMetadata(accountId)
      .then((data) => {
        if (!cancelled) setVariableMetadata(data);
      })
      .catch(() => {
        if (!cancelled) setVariableMetadata(null);
      })
      .finally(() => {
        if (!cancelled) setVariableMetadataLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const convertToWhatsAppName = (displayName: string): string => {
    if (!displayName) return '';
    let apiName = displayName.toLowerCase();
    apiName = apiName.replace(/[\s-]+/g, '_');
    apiName = apiName.replace(/[^a-z0-9_]/g, '');
    apiName = apiName.replace(/_+/g, '_');
    apiName = apiName.replace(/^_|_$/g, '');
    if (apiName && !/^[a-z]/.test(apiName)) {
      apiName = 'template_' + apiName;
    }
    return apiName.slice(0, 512);
  };

  const convertedName = convertToWhatsAppName(name);

  useEffect(() => {
    if (!channelId || !convertedName) {
      setNameCheckAvailable(null);
      setNameCheckMessage('');
      return;
    }
    const timer = setTimeout(async () => {
      setNameCheckLoading(true);
      setNameCheckAvailable(null);
      setNameCheckMessage('');
      try {
        const res = await whatsappTemplatesApi.checkTemplateNameAvailable(
          channelId,
          convertedName,
          language || 'en',
          initialTemplate?.id
        );
        setNameCheckAvailable(res.available);
        setNameCheckMessage(res.message || '');
      } catch {
        setNameCheckAvailable(false);
        setNameCheckMessage('Could not verify name with WhatsApp.');
      } finally {
        setNameCheckLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [channelId, convertedName, language, initialTemplate?.id]);

  const defaultConstraints: WhatsAppConstraints = {
    header: {
      text_max_length: 60,
      media_types: ['IMAGE', 'VIDEO', 'DOCUMENT'],
    },
    body: {
      max_length: 1024,
      max_variables: 10,
    },
    footer: {
      max_length: 60,
      allow_variables: false,
    },
    buttons: {
      max_count: 3,
      max_text_length: 25,
      allowed_types: ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'],
    },
    variables: {
      max_total: 10,
      format: '{{N}}',
    },
  };

  const activeConstraints = constraints || defaultConstraints;

  useEffect(() => {
    const variablePattern = /\{\{(\d+)\}\}/g;
    const matches = body.match(variablePattern);
    if (matches) {
      const count = matches.length;
      const newVars = Array.from({ length: count }, (_, i) => `var_${i + 1}`);
      setVariables((prev) => newVars.map((v, i) => prev[i] || v));
    }
  }, [body]);

  useEffect(() => {
    let previewText = '';

    if (headerType === 'TEXT' && headerText) {
      previewText += `*${headerText}*\n\n`;
    } else if (headerType !== 'NONE' && headerType !== 'TEXT') {
      previewText += `[${headerType}]\n\n`;
    }

    let bodyPreview = body;
    variables.forEach((varName, index) => {
      const placeholder = `{{${index + 1}}}`;
      bodyPreview = bodyPreview.replace(
        new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'),
        `*${varName}*`
      );
    });
    previewText += bodyPreview;

    if (footer) {
      previewText += `\n\n_${footer}_`;
    }

    if (buttons.length > 0) {
      previewText += '\n\n';
      buttons.forEach((btn, index) => {
        previewText += `\n[${index + 1}] ${btn.text}`;
      });
    }

    setPreview(previewText);
  }, [body, footer, headerType, headerText, variables, buttons]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name || name.length === 0) {
      newErrors.name = 'Template name is required';
    } else if (!convertedName) {
      newErrors.name = 'Template name must contain at least one letter or number';
    }

    if (!body || body.length === 0) {
      newErrors.body = 'Message body is required';
    } else if (body.length > activeConstraints.body.max_length) {
      newErrors.body = `Body exceeds maximum length of ${activeConstraints.body.max_length} characters`;
    }

    if (headerType === 'TEXT' && headerText.length > activeConstraints.header.text_max_length) {
      newErrors.header = `Header text exceeds maximum length of ${activeConstraints.header.text_max_length} characters`;
    }

    if (footer && footer.length > activeConstraints.footer.max_length) {
      newErrors.footer = `Footer exceeds maximum length of ${activeConstraints.footer.max_length} characters`;
    }

    if (buttons.length > activeConstraints.buttons.max_count) {
      newErrors.buttons = `Maximum ${activeConstraints.buttons.max_count} buttons allowed`;
    }

    buttons.forEach((btn, index) => {
      if (btn.text.length > activeConstraints.buttons.max_text_length) {
        newErrors[`button_${index}`] = `Button text exceeds ${activeConstraints.buttons.max_text_length} characters`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInsertVariableByName = (variableName: string) => {
    if (variables.length >= activeConstraints.body.max_variables) return;
    const variableNumber = variables.length + 1;
    const placeholder = `{{${variableNumber}}}`;
    setBody((prev) => prev + placeholder);
    setVariables((prev) => [...prev, variableName]);
  };

  const handleInsertVariable = () => {
    if (variables.length >= activeConstraints.body.max_variables) return;
    const variableNumber = variables.length + 1;
    const placeholder = `{{${variableNumber}}}`;
    setBody((prev) => prev + placeholder);
    setVariables((prev) => [...prev, `variable_${variableNumber}`]);
  };

  const handleRemoveVariable = (index: number) => {
    const placeholderToRemove = `{{${index + 1}}}`;
    const rePlaceholder = new RegExp(placeholderToRemove.replace(/[{}]/g, '\\$&'), 'g');
    setBody((prev) => {
      const without = prev.replace(rePlaceholder, '');
      const matches = [...without.matchAll(/\{\{\d+\}\}/g)];
      if (matches.length === 0) return without.replace(/\s+/g, ' ').trim();
      let result = without;
      for (let i = matches.length - 1; i >= 0; i--) {
        const m = matches[i];
        const start = m.index!;
        const end = start + m[0].length;
        result = result.slice(0, start) + `{{${i + 1}}}` + result.slice(end);
      }
      return result.replace(/\s+/g, ' ').trim();
    });
    setVariables((prev) => prev.filter((_, i) => i !== index));
  };

  // 2026-08-07: variables previously had no rename path at all — typing
  // {{1}}, {{2}}... in the body auto-named them var_1, var_2 with no way to
  // change that, so anything sending named variables (e.g. the ticketing
  // agent-notify flow's ticket_number/category_label/summary/contact_phone/
  // ticket_url) could only ever match by accident. Sanitized to a safe
  // identifier shape since these names are matched literally, as dict keys,
  // by whatever sends the template — spaces or symbols would just never match.
  const sanitizeVariableName = (raw: string, index: number): string => {
    const cleaned = raw
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_')
      .replace(/^_+|_+$/g, '');
    return cleaned || `var_${index + 1}`;
  };

  const handleRenameVariable = (index: number, rawName: string) => {
    setVariables((prev) => prev.map((v, i) => (i === index ? rawName : v)));
  };

  const handleRenameVariableBlur = (index: number) => {
    setVariables((prev) =>
      prev.map((v, i) => (i === index ? sanitizeVariableName(v, index) : v))
    );
  };

  const handleAddButton = () => {
    if (buttons.length < activeConstraints.buttons.max_count) {
      setButtons([...buttons, { type: 'QUICK_REPLY', text: '' }]);
    }
  };

  const handleRemoveButton = (index: number) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const handleButtonChange = (index: number, field: string, value: unknown) => {
    const updated = [...buttons];
    updated[index] = { ...updated[index], [field]: value };
    setButtons(updated);
  };

  const handleSave = async () => {
    setSaveError(null);

    if (!validate()) {
      return;
    }

    setSaving(true);
    try {
      const templateData: TemplateCreateRequest = {
        name,
        category,
        language,
        body,
        footer: footer || undefined,
        variables,
        header:
          headerType !== 'NONE'
            ? {
                type: headerType,
                text: headerType === 'TEXT' ? headerText : undefined,
                media_url: headerType !== 'TEXT' ? headerMediaUrl : undefined,
              }
            : undefined,
        buttons: buttons.length > 0 ? buttons : undefined,
      };

      await onSave(templateData);
    } catch (error: unknown) {
      console.error('Error saving template:', error);
      const msg = error instanceof Error ? error.message : 'Failed to save template. Please check your input and try again.';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const nameHelper =
    errors.name ||
    (nameCheckMessage && !nameCheckLoading ? nameCheckMessage : null) ||
    (name && convertedName
      ? `WhatsApp API name: ${convertedName}. The check does not submit the template.`
      : 'Enter any name — it will be auto-converted for WhatsApp');

  const maxVarsReached = variables.length >= activeConstraints.body.max_variables;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-5">
        {saveError && (
          <Alert variant="destructive" className="flex flex-row items-start justify-between gap-2">
            <AlertDescription className="min-w-0 flex-1">{saveError}</AlertDescription>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-destructive-foreground hover:bg-destructive/20"
              onClick={() => setSaveError(null)}
              aria-label="Dismiss"
            >
              <XCircle className="h-4 w-4" />
            </Button>
          </Alert>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="space-y-5 lg:col-span-7">
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-base">Template details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="tpl-name">Template name</Label>
                  <div className="relative">
                    <Input
                      id="tpl-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="RentRemote Welcome Message"
                      className={cn('pr-10', errors.name && 'border-destructive')}
                      aria-invalid={!!errors.name}
                    />
                    {channelId && (name || nameCheckLoading) ? (
                      <div className="pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center">
                        {nameCheckLoading ? (
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        ) : nameCheckAvailable === true ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="pointer-events-auto inline-flex">
                                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>Name is available for submission on WhatsApp</TooltipContent>
                          </Tooltip>
                        ) : nameCheckAvailable === false ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="pointer-events-auto inline-flex">
                                <XCircle className="h-5 w-5 text-destructive" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{nameCheckMessage || 'Name already in use on WhatsApp'}</TooltipContent>
                          </Tooltip>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <p className={cn('text-xs', errors.name ? 'text-destructive' : 'text-muted-foreground')}>{nameHelper}</p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Category</Label>
                    <Select value={category} onValueChange={(v) => setCategory(v as WhatsAppTemplateCategory)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={WhatsAppTemplateCategory.UTILITY}>Utility</SelectItem>
                        <SelectItem value={WhatsAppTemplateCategory.MARKETING}>Marketing</SelectItem>
                        <SelectItem value={WhatsAppTemplateCategory.AUTHENTICATION}>Authentication</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Language</Label>
                    <Select value={language} onValueChange={setLanguage}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Spanish</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                        <SelectItem value="de">German</SelectItem>
                        <SelectItem value="pt">Portuguese</SelectItem>
                        <SelectItem value="hi">Hindi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <Label className="text-sm font-medium">Header (optional)</Label>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {HEADER_TYPES.map(({ value, label }) => (
                      <label key={value} className="flex cursor-pointer items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="headerType"
                          value={value}
                          checked={headerType === value}
                          onChange={() => setHeaderType(value)}
                          className="h-4 w-4 accent-primary"
                        />
                        {label}
                      </label>
                    ))}
                  </div>

                  {headerType === 'TEXT' && (
                    <div className="space-y-1.5 pt-1">
                      <Label htmlFor="header-text">Header text</Label>
                      <Input
                        id="header-text"
                        value={headerText}
                        onChange={(e) => setHeaderText(e.target.value)}
                        className={errors.header ? 'border-destructive' : ''}
                      />
                      <p className={cn('text-xs', errors.header ? 'text-destructive' : 'text-muted-foreground')}>
                        {errors.header || `Max ${activeConstraints.header.text_max_length} characters`}
                      </p>
                    </div>
                  )}

                  {headerType !== 'NONE' && headerType !== 'TEXT' && (
                    <div className="space-y-1.5 pt-1">
                      <Label htmlFor="header-media">Media URL</Label>
                      <Input
                        id="header-media"
                        value={headerMediaUrl}
                        onChange={(e) => setHeaderMediaUrl(e.target.value)}
                        placeholder={
                          headerType === 'VIDEO'
                            ? 'https://example.com/video.mp4'
                            : headerType === 'IMAGE'
                              ? 'https://example.com/image.jpg'
                              : 'https://example.com/document.pdf'
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        Must be a direct link to the file (e.g. .mp4, .jpg). YouTube, Vimeo, or other page links are not
                        supported for template submission.
                      </p>
                    </div>
                  )}
                </div>

                <Separator />

                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-sm font-medium">Message body *</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1"
                          onClick={handleInsertVariable}
                          disabled={maxVarsReached}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Insert variable
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Insert variable placeholder (generic)</TooltipContent>
                    </Tooltip>
                  </div>

                  {(variableMetadata || variableMetadataLoading) && (
                    <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Available variables (click to insert)
                      </p>
                      {variableMetadataLoading ? (
                        <p className="text-xs text-muted-foreground">Loading variables…</p>
                      ) : variableMetadata ? (
                        <>
                          {variableMetadata.system_variables?.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-primary">System</p>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {variableMetadata.system_variables.map((v: VariableMetadataItem) => (
                                  <Tooltip key={v.name}>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex">
                                        <Badge
                                          variant="outline"
                                          className={cn(
                                            'cursor-pointer font-normal',
                                            maxVarsReached && 'pointer-events-none opacity-50'
                                          )}
                                          onClick={() => {
                                            if (!maxVarsReached) handleInsertVariableByName(v.name);
                                          }}
                                        >
                                          {v.name}
                                        </Badge>
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>{v.label || v.name}</TooltipContent>
                                  </Tooltip>
                                ))}
                              </div>
                            </div>
                          )}
                          {variableMetadata.ticket_variables && variableMetadata.ticket_variables.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-primary">Ticketing</p>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {variableMetadata.ticket_variables.map((v: VariableMetadataItem) => (
                                  <Tooltip key={v.name}>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex">
                                        <Badge
                                          variant="outline"
                                          className={cn(
                                            'cursor-pointer font-normal',
                                            maxVarsReached && 'pointer-events-none opacity-50'
                                          )}
                                          onClick={() => {
                                            if (!maxVarsReached) handleInsertVariableByName(v.name);
                                          }}
                                        >
                                          {v.name}
                                        </Badge>
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>{v.label || v.name}</TooltipContent>
                                  </Tooltip>
                                ))}
                              </div>
                            </div>
                          )}
                          {variableMetadata.custom_variables?.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-secondary-foreground">Custom</p>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {variableMetadata.custom_variables.map((v: CustomVariableItem) => (
                                  <Tooltip key={v.name}>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex">
                                        <Badge
                                          variant="secondary"
                                          className={cn(
                                            'cursor-pointer font-normal',
                                            maxVarsReached && 'pointer-events-none opacity-50'
                                          )}
                                          onClick={() => {
                                            if (!maxVarsReached) handleInsertVariableByName(v.name);
                                          }}
                                        >
                                          {v.name}
                                        </Badge>
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>{v.label || v.name}</TooltipContent>
                                  </Tooltip>
                                ))}
                              </div>
                            </div>
                          )}
                          {!variableMetadata.system_variables?.length &&
                            !variableMetadata.ticket_variables?.length &&
                            !variableMetadata.custom_variables?.length && (
                              <p className="text-xs text-muted-foreground">
                                No variables defined. Use “Insert variable” above.
                              </p>
                            )}
                        </>
                      ) : null}
                    </div>
                  )}

                  <Textarea
                    rows={6}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Hi {{1}}, your order #{{2}} is ready for pickup!"
                    className={cn('min-h-[140px] resize-y', errors.body && 'border-destructive')}
                    aria-invalid={!!errors.body}
                  />
                  <p className={cn('text-xs', errors.body ? 'text-destructive' : 'text-muted-foreground')}>
                    {errors.body ||
                      `${body.length}/${activeConstraints.body.max_length} characters. Use {{1}}, {{2}} for variables.`}
                  </p>

                  {variables.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        Variables — rename to match what your sending code will provide
                        (e.g. <code className="text-[11px]">order_id</code>,{' '}
                        <code className="text-[11px]">customer_name</code>). Matched by this
                        exact name at send time, so a default like <code className="text-[11px]">var_1</code>{' '}
                        won't line up with anything unless you rename it.
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {variables.map((varName, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-1 rounded-md border border-input bg-background pl-2 pr-1"
                          >
                            <span className="text-xs text-muted-foreground">{`{{${index + 1}}} =`}</span>
                            <Input
                              value={varName}
                              onChange={(e) => handleRenameVariable(index, e.target.value)}
                              onBlur={() => handleRenameVariableBlur(index)}
                              aria-label={`Variable ${index + 1} name`}
                              className="h-6 w-28 border-0 px-1 text-xs shadow-none focus-visible:ring-1"
                            />
                            <button
                              type="button"
                              className="rounded-sm p-0.5 hover:bg-muted"
                              onClick={() => handleRemoveVariable(index)}
                              aria-label={`Remove variable ${index + 1}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="tpl-footer">Footer (optional)</Label>
                  <Input
                    id="tpl-footer"
                    value={footer}
                    onChange={(e) => setFooter(e.target.value)}
                    placeholder="Reply STOP to unsubscribe"
                    className={errors.footer ? 'border-destructive' : ''}
                  />
                  <p className={cn('text-xs', errors.footer ? 'text-destructive' : 'text-muted-foreground')}>
                    {errors.footer || `Max ${activeConstraints.footer.max_length} characters`}
                  </p>
                </div>

                <Separator />

                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-sm font-medium">Buttons (optional)</Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1"
                      onClick={handleAddButton}
                      disabled={buttons.length >= activeConstraints.buttons.max_count}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add button
                    </Button>
                  </div>

                  {buttons.map((button, index) => (
                    <Card key={index} className="border-border/80">
                      <CardContent className="space-y-4 pt-4">
                        <div className="flex gap-3">
                          <div className="min-w-0 flex-1 space-y-4">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              <div className="space-y-1.5">
                                <Label>Type</Label>
                                <Select
                                  value={button.type}
                                  onValueChange={(v) => handleButtonChange(index, 'type', v)}
                                >
                                  <SelectTrigger className="h-9">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="QUICK_REPLY">Quick reply</SelectItem>
                                    <SelectItem value="URL">URL</SelectItem>
                                    <SelectItem value="PHONE_NUMBER">Phone number</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1.5">
                                <Label htmlFor={`btn-text-${index}`}>Button text</Label>
                                <Input
                                  id={`btn-text-${index}`}
                                  value={button.text}
                                  onChange={(e) => handleButtonChange(index, 'text', e.target.value)}
                                  className={errors[`button_${index}`] ? 'border-destructive' : ''}
                                />
                                {errors[`button_${index}`] && (
                                  <p className="text-xs text-destructive">{errors[`button_${index}`]}</p>
                                )}
                              </div>
                            </div>
                            {button.type === 'URL' && (
                              <div className="space-y-1.5">
                                <Label htmlFor={`btn-url-${index}`}>URL</Label>
                                <Input
                                  id={`btn-url-${index}`}
                                  value={button.url || ''}
                                  onChange={(e) => handleButtonChange(index, 'url', e.target.value)}
                                  placeholder="https://example.com"
                                />
                              </div>
                            )}
                            {button.type === 'PHONE_NUMBER' && (
                              <div className="space-y-1.5">
                                <Label htmlFor={`btn-phone-${index}`}>Phone number</Label>
                                <Input
                                  id={`btn-phone-${index}`}
                                  value={button.phone_number || ''}
                                  onChange={(e) => handleButtonChange(index, 'phone_number', e.target.value)}
                                  placeholder="+1234567890"
                                />
                              </div>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="mt-6 shrink-0 text-destructive hover:text-destructive sm:mt-0"
                            onClick={() => handleRemoveButton(index)}
                            aria-label="Remove button"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {buttons.length === 0 && (
                    <Alert>
                      <AlertDescription className="text-sm">
                        No buttons added. Buttons are optional but can improve engagement.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>

                <Separator />

                <div className="flex flex-wrap justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={handleSave} disabled={saving} className="gap-1.5">
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {saving ? 'Saving…' : initialTemplate ? 'Update template' : 'Create template'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-5">
            <div className="space-y-5 lg:sticky lg:top-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Eye className="h-4 w-4 text-muted-foreground" />
                    Preview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Card className="border-sky-200 bg-sky-50 dark:border-sky-900 dark:bg-sky-950/40">
                    <CardContent className="pt-4">
                      <p className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
                        {preview || 'Your template preview will appear here…'}
                      </p>
                    </CardContent>
                  </Card>
                  <div className="mt-5 space-y-3">
                    <p className="text-xs font-medium text-muted-foreground">Template info</p>
                    <div className="flex flex-wrap gap-1.5">
                      <Badge variant="outline">Category: {category}</Badge>
                      <Badge variant="outline">Language: {language}</Badge>
                      <Badge variant="outline">
                        Variables: {variables.length}/{activeConstraints.body.max_variables}
                      </Badge>
                      <Badge variant="outline">
                        Buttons: {buttons.length}/{activeConstraints.buttons.max_count}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default TemplateBuilder;
